import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import {
  fileToDataUrl, newPrivilegeCode, PRIVILEGE_UNSUPPORTED_FIELDS,
  toAuditEntriesByBenefitCode, toCrmPrivilegePatch, toUiPrivilege,
} from '../adapters';
import type { AuditLogEntry, Tier as CrmTier } from '../crmClient';
import { PrivilegeScreen } from '../../screens/PrivilegeScreen';
import type { Privilege, TierId } from '../../screens/loyaltyData';

/**
 * ต่อหน้าสิทธิพิเศษแบบกดรับเข้ากับ CRM module (FR-ADM-19)
 *
 * หนึ่งสิทธิ์ = benefit code หนึ่งตัว พร้อมเงื่อนไขที่ผูกกับระดับสมาชิก
 * ลูกค้ากดรับแล้วได้ "รหัส" ที่มีอายุเป็นนาที ไม่ใช่คูปอง
 *
 * ⚠️ **เงื่อนไขเป็นชุดเดียวทุกระดับที่เลือก** — backend เขียนค่าเดียวกันลงทุกความสัมพันธ์
 *    ตั้งอายุรหัสหรือเพดานต่างกันรายระดับจากหน้านี้ไม่ได้
 * ⚠️ หน้าระดับสมาชิกแก้เพดานต่อคนของสิทธิ์เดียวกันได้ด้วย — แก้คนละที่จะทับกัน
 * ระดับสมาชิกอ้างด้วย id ของ backend — ตั้งชื่ออะไรก็ได้และมีกี่ระดับก็ได้
 */
export function PrivilegeContainer() {
  const { api, dataMode } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [privileges, setPrivileges] = useState<Privilege[] | undefined>(undefined);
  const [tiers, setTiers] = useState<CrmTier[] | undefined>(undefined);
  const [memberCounts, setMemberCounts] = useState<Map<string, number>>(new Map());
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /** ประวัติการแก้ไขสิทธิ์ทั้ง division — ของเสริม ล้มแล้วหน้ายังใช้ได้ (null = ไม่แสดงส่วนประวัติ) */
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[] | null>(null);
  const loadAudit = useCallback(
    () => api.listAuditLogs('benefit-code', 200).then((p) => setAuditLogs(p.items)).catch(() => setAuditLogs(null)),
    [api],
  );

  /** id ของระดับ (ตัวเลขของ backend) ↔ id ที่หน้าจอใช้ (ข้อความ) */
  const tierKeyById = useMemo(
    () => new Map((tiers ?? []).map((t) => [t.id, String(t.id)] as const)),
    [tiers],
  );
  const policyIdByKey = useMemo(
    () => new Map<TierId, number>((tiers ?? []).map((t) => [String(t.id), t.id] as const)),
    [tiers],
  );

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');

    Promise.all([
      api.listPrivileges(),
      api.listTiers(),
      /* จำนวนสมาชิกต่อระดับเป็นของเสริมของช่องกลุ่มเป้าหมาย ล้มแล้วหน้ายังใช้ได้ */
      api.listTierSummary().catch(() => null),
    ])
      .then(([page, tierPage, summary]) => {
        if (cancelled) return;
        const byId = new Map(tierPage.items.map((t) => [t.id, String(t.id)] as const));
        setTiers(tierPage.items);
        setPrivileges(page.items.map((b) => toUiPrivilege(b, byId)));
        setMemberCounts(
          summary
            ? new Map(summary.map((sum) => [String(sum.tier.id), sum.totalAccounts]))
            : new Map(),
        );
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [isMock, api]);

  useEffect(() => load(), [load]);
  useEffect(() => { if (!isMock) void loadAudit(); }, [isMock, loadAudit]);

  const auditByPrivilege = useMemo(() => {
    if (!auditLogs) return undefined;
    const tierName = (id: number) => tiers?.find((t) => t.id === id)?.name;
    const benefitName = (id: number) => privileges?.find((p) => p.id === String(id))?.name;
    return toAuditEntriesByBenefitCode(auditLogs, { tierName, benefitName });
  }, [auditLogs, tiers, privileges]);

  const tierOptions = useMemo(
    () => (tiers ?? []).map((t) => {
      const id = String(t.id);
      return { id, name: t.name, memberCount: memberCounts.get(id) ?? 0 };
    }),
    [tiers, memberCounts],
  );

  /**
   * บันทึกสิทธิ์หนึ่งรายการ
   *
   * id ของรายการใหม่ที่หน้าจอสร้างเป็น `pv-<timestamp>` ซึ่งไม่ใช่เลข
   * ใช้ตรงนี้แยกว่าเป็นการสร้างใหม่หรือแก้ของเดิม
   */
  const onSave = useCallback(async (p: Privilege, changeReason?: string) => {
    const patch = { ...toCrmPrivilegePatch(p, policyIdByKey), changeReason };
    const id = Number(p.id);
    const isNew = !(Number.isInteger(id) && id > 0);

    /* รูปเดิมถูกเอาออก — ล้างด้วย PATCH ปกติ ดูจากรายการที่โหลดมา ไม่ใช่จากค่าที่หน้าจอส่งมา */
    const had = privileges?.find((x) => x.id === p.id)?.imageUrl;
    if (!isNew && had && !p.imageUrl) patch.image = null;

    /* รูปใหม่ไปกับคำขอเดียวกัน — ไม่มีจังหวะที่ข้อมูลบันทึกแล้วแต่รูปยังไม่ขึ้น */
    if (p.imageFile) patch.image = await fileToDataUrl(p.imageFile);

    if (isNew) {
      /* 🔧 ส่ง code เองเพื่อเลี่ยงบั๊กตอนสร้างของระบบหลังบ้าน — ดู newPrivilegeCode */
      await api.createPrivilege({ ...patch, code: newPrivilegeCode(p.benefitKind ?? 'percent') });
    } else {
      await api.updatePrivilege(id, patch);
    }

    const fresh = await api.listPrivileges();
    setPrivileges(fresh.items.map((b) => toUiPrivilege(b, tierKeyById)));
    void loadAudit();
  }, [api, policyIdByKey, tierKeyById, privileges, loadAudit]);

  if (isMock) return <PrivilegeScreen initialState="ready" />;

  return (
    <PrivilegeScreen
      initialState={state}
      privileges={privileges ?? []}
      tiers={tierOptions}
      onSave={onSave}
      onRefresh={load}
      refreshing={state === 'loading'}
      unsupportedFields={PRIVILEGE_UNSUPPORTED_FIELDS}
      auditByPrivilege={auditByPrivilege}
    />
  );
}
